// templates/MedicalCertTemplate.vue
<template>
  <div class="mc-doc">
    <!-- Letterhead -->
    <div class="mc-head" :class="{ 'has-logo': hasLogo }">
      <slot name="header-toolbar" />
      <img
        v-if="hasLogo"
        :src="documentData.logoUrl"
        :alt="clinicName"
        class="mc-logo"
      />
      <div class="mc-clinic">
        <div class="mc-clinic-name">
          <template v-if="headerFormat === 'clinic_branch'">
            {{ clinicName || BLANK
            }}<span v-if="branchName" class="mc-branch"> · {{ branchName }}</span>
          </template>
          <template v-else-if="headerFormat === 'branch'">
            {{ branchName || clinicName || BLANK }}
          </template>
          <template v-else>
            {{ clinicName || BLANK }}
          </template>
        </div>
        <div v-if="branchInfo && branchInfo.address" class="mc-clinic-addr">
          {{ branchInfo.address }}
        </div>
        <div v-if="hasMeta" class="mc-clinic-meta">
          <span v-if="branchInfo && branchInfo.tel"
            >{{ isTh ? "โทร." : "Tel." }} {{ branchInfo.tel }}</span
          >
          <template v-if="branchInfo && branchInfo.website">
            <span v-if="branchInfo && branchInfo.tel" class="mc-sep">·</span>
            <span>{{ branchInfo.website }}</span>
          </template>
          <template v-if="taxId">
            <span
              v-if="branchInfo && (branchInfo.tel || branchInfo.website)"
              class="mc-sep"
              >·</span
            >
            <span>{{ isTh ? "เลขผู้เสียภาษี" : "Tax ID" }} {{ taxId }}</span>
          </template>
        </div>
      </div>
      <div class="mc-ref">
        <div>
          <span class="mc-ref-lbl">{{ isTh ? "วันที่" : "Date" }}</span>
          <span class="mc-ref-val">{{ todayDisplay || BLANK }}</span>
        </div>
      </div>
    </div>

    <!-- Title -->
    <div class="mc-title">
      <div class="mc-title-name">
        {{ isTh ? "ใบรับรองแพทย์" : "Medical Certificate" }}
      </div>
    </div>

    <!-- Doctor declaration -->
    <p class="mc-declare">
      <span v-if="isTh">ข้าพเจ้า </span>
      <span v-else>I, the undersigned, </span>
      <slot name="doctor-token" :doctor-full-name="doctorFullName">
        <span class="mc-underline">{{ doctorFullName }}</span>
      </slot>
      <span v-if="isTh"> ผู้ประกอบวิชาชีพทันตกรรม ใบอนุญาตเลขที่ </span>
      <span v-else>, a registered dental practitioner, licence number </span>
      <span class="mc-underline">{{ licenseDisplay }}</span>
      <span v-if="isTh">
        ขอรับรองว่าได้ทำการตรวจและให้การรักษาทางทันตกรรมแก่ผู้ป่วยรายดังต่อไปนี้
      </span>
      <span v-else
        >, hereby certify that I have personally examined and rendered dental
        treatment to the patient identified below.</span
      >
    </p>

    <!-- Patient fields -->
    <div class="mc-fields">
      <div class="mc-k">{{ isTh ? "ผู้ป่วย" : "Patient" }}</div>
      <div class="mc-v">
        <span v-if="patientName">{{ patientName }}</span>
        <span v-else class="mc-blank-inline"></span>
      </div>
      <div class="mc-k">{{ isTh ? "อายุ" : "Age" }}</div>
      <div class="mc-v">
        <span v-if="patientAge !== null">{{ ageDisplay }}</span>
        <span v-else class="mc-blank-inline"></span>
      </div>

      <div class="mc-k">DN</div>
      <div class="mc-v">
        <span v-if="documentData.patient.dn">{{ documentData.patient.dn }}</span>
        <span v-else class="mc-blank-inline"></span>
      </div>
      <div class="mc-k">{{ isTh ? "บัตรประชาชน" : "ID / Passport" }}</div>
      <div class="mc-v">
        <span v-if="documentData.patient.personalId">{{
          documentData.patient.personalId
        }}</span>
        <span v-else class="mc-blank-inline"></span>
      </div>
    </div>

    <!-- Section 01 — Procedures -->
    <div class="mc-section">
      <div class="mc-section-num">01</div>
      <div class="mc-section-t">
        {{ isTh ? "หัตถการที่ได้รับ" : "Procedures rendered" }}
      </div>
    </div>
    <table v-if="sortedItems.length" class="mc-table">
      <thead>
        <tr>
          <th class="mc-col-date">{{ isTh ? "วันที่" : "Date" }}</th>
          <th class="mc-col-tooth">{{ isTh ? "ซี่ฟัน" : "Tooth" }}</th>
          <th>{{ isTh ? "การวินิจฉัย" : "Diagnosis" }}</th>
          <th>{{ isTh ? "หัตถการ" : "Procedure" }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(it, i) in sortedItems" :key="i">
          <td class="mc-cell-date">{{ formatDate(it.creationDt) }}</td>
          <td>
            {{ it.toothNumberSurfaces ? it.toothNumberSurfaces.trim() : "" }}
          </td>
          <td>{{ it.dxName || "" }}</td>
          <td>{{ it.name || "" }}</td>
        </tr>
      </tbody>
    </table>
    <div v-else class="mc-rules">
      <div v-for="n in 4" :key="n" class="mc-rule"></div>
    </div>

    <!-- Section 02 — Opinion -->
    <div class="mc-section">
      <div class="mc-section-num">02</div>
      <div class="mc-section-t">
        {{ isTh ? "ความเห็นของทันตแพทย์" : "Professional opinion" }}
      </div>
    </div>
    <slot name="comment" :text="documentData.commentDetail">
      <p v-if="documentData.commentDetail" class="mc-opinion no-indent">
        {{ documentData.commentDetail }}
      </p>
      <div v-else class="mc-rules">
        <div v-for="n in 4" :key="n" class="mc-rule"></div>
      </div>
    </slot>

    <!-- Signature -->
    <div class="mc-sig">
      <div class="mc-sig-col">
        <div class="mc-sig-line"></div>
        <div class="mc-sig-name">({{ doctorFullName }})</div>
        <div class="mc-sig-role">
          {{ isTh ? "ทันตแพทย์ผู้ตรวจรักษา" : "Attending dentist" }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";

const BLANK = "_______";

export default {
  name: "MedicalCertTemplate",
  props: {
    documentData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return { BLANK };
  },
  computed: {
    ...mapGetters({
      branchInfo: "moduleUser/getBranchInfo",
    }),
    isTh() {
      return (this.documentData.lang || "th") !== "en";
    },
    clinicName() {
      return (
        (this.branchInfo &&
          this.branchInfo.clinic &&
          this.branchInfo.clinic.name) ||
        ""
      );
    },
    branchName() {
      return (this.branchInfo && this.branchInfo.name) || "";
    },
    hasLogo() {
      return Boolean(
        this.documentData.showLogo && this.documentData.logoUrl
      );
    },
    headerFormat() {
      const f = this.documentData.headerFormat;
      return ["clinic_branch", "clinic", "branch"].includes(f)
        ? f
        : "clinic_branch";
    },
    taxId() {
      return (
        (this.branchInfo &&
          this.branchInfo.clinic &&
          (this.branchInfo.clinic.taxId ||
            this.branchInfo.clinic.vatRegistrationNo)) ||
        (this.branchInfo && this.branchInfo.vatRegistrationNo) ||
        ""
      );
    },
    hasMeta() {
      return Boolean(
        this.branchInfo &&
          (this.branchInfo.tel || this.branchInfo.website || this.taxId)
      );
    },
    licenseDisplay() {
      const v = this.documentData.doctorObj && this.documentData.doctorObj.drLicenseNo;
      if (!v || v === "null" || v === "undefined") return BLANK;
      return v;
    },
    doctorFullName() {
      const title = this.documentData.title || "";
      const name =
        (this.documentData.doctorObj && this.documentData.doctorObj.fullName) ||
        "";
      const composed = `${title}${name}`.trim();
      return composed || BLANK;
    },
    patientName() {
      if (!this.isTh && this.documentData.ptFullnameEn) {
        return this.documentData.ptFullnameEn;
      }
      return this.documentData.ptFullname || "";
    },
    todayDisplay() {
      const today = this.documentData.today;
      if (!this.isTh && this.documentData.todayEn) {
        return this.documentData.todayEn;
      }
      return today || "";
    },
    patientAge() {
      const dob = this.documentData.patient && this.documentData.patient.dob;
      if (!dob) return null;
      const date = moment(dob, "YYYY-MM-DD");
      if (!date.isValid()) return null;
      return moment().diff(date, "years");
    },
    ageDisplay() {
      if (this.patientAge === null) return BLANK;
      return this.isTh
        ? `${this.patientAge} ปี`
        : `${this.patientAge} yrs`;
    },
    sortedItems() {
      const items = (this.documentData.selectedItem || []).slice();
      return items.sort(
        (a, b) => Date.parse(a.creationDt) - Date.parse(b.creationDt)
      );
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return BLANK;
      if (this.isTh) {
        return moment(date)
          .add(543, "years")
          .locale("th")
          .format("D MMMM YYYY");
      }
      return moment(date).locale("en").format("D MMMM YYYY");
    },
  },
};
</script>

<style lang="scss" scoped>
.mc-doc {
  font-family: "Sarabun", sans-serif;
  font-size: 13.5px;
  line-height: 1.7;
  color: #161616;
}

.mc-head {
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 24px;
  align-items: start;
  padding-bottom: 16px;
  margin-bottom: 22px;
  border-bottom: 1.25px solid #161616;
  position: relative;
}

.mc-head.has-logo {
  grid-template-columns: auto 1fr auto;
}

.mc-logo {
  width: 64px;
  height: 64px;
  object-fit: contain;
  align-self: start;
}

.mc-clinic {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.mc-clinic-name {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.25;
}

.mc-branch {
  color: #5a5a5a;
  font-weight: 500;
}

.mc-clinic-addr {
  font-size: 12px;
  color: #5a5a5a;
  line-height: 1.55;
  white-space: pre-line;
}

.mc-clinic-meta {
  font-size: 11px;
  color: #9a9a96;
  line-height: 1.55;
  letter-spacing: 0.02em;
  margin-top: 2px;
}

.mc-sep {
  margin: 0 7px;
  opacity: 0.5;
}

.mc-ref {
  font-size: 12px;
  line-height: 1.5;
  text-align: right;
  color: #5a5a5a;
  white-space: nowrap;
}

.mc-ref-lbl {
  color: #9a9a96;
  margin-right: 8px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 10px;
}

.mc-ref-val {
  font-weight: 500;
  color: #161616;
}

.mc-title {
  text-align: center;
  margin: 0 0 24px;
}

.mc-title-name {
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1.1;
}

.mc-declare {
  margin: 0 0 10px;
  text-indent: 3em;
  text-align: justify;
  line-height: 2.1;
}

.mc-underline {
  border-bottom: 0.5px solid #161616;
  padding: 0 4px 1px;
  display: inline;
  font-weight: 600;
}

.mc-fields {
  margin: 16px 0;
  display: grid;
  grid-template-columns: auto 1fr auto 1fr;
  column-gap: 12px;
  row-gap: 8px;
  align-items: baseline;
  font-size: 13px;
}

.mc-fields .mc-k + .mc-v {
  margin-right: 32px;
}

.mc-fields .mc-v:last-child {
  margin-right: 0;
}

.mc-k {
  color: #9a9a96;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding-top: 1px;
}

.mc-v {
  color: #161616;
  font-weight: 500;
}

.mc-blank-inline {
  display: inline-block;
  min-width: 140px;
  border-bottom: 0.5px solid #161616;
  height: 1em;
}

.mc-section {
  margin: 22px 0 10px;
  display: inline-flex;
  align-items: baseline;
  gap: 14px;
  width: fit-content;
  padding-bottom: 4px;
  border-bottom: 1.25px solid #161616;
}

.mc-section-num {
  font-size: 11px;
  color: #9a9a96;
  letter-spacing: 0.1em;
}

.mc-section-t {
  font-weight: 600;
  font-size: 13.5px;
  letter-spacing: 0.02em;
}

.mc-table {
  width: 100%;
  border-collapse: collapse;
  margin: 4px 0;
  font-size: 12px;
}

.mc-table th,
.mc-table td {
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.18);
  padding: 6px 4px;
  text-align: left;
  vertical-align: top;
}

.mc-table thead th {
  border-bottom: 0.5px solid #161616;
  border-top: 0.5px solid #161616;
  font-weight: 500;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9a9a96;
}

.mc-table tbody tr:last-child td {
  border-bottom: none;
}

.mc-col-date {
  width: 22%;
}

.mc-col-tooth {
  width: 14%;
}

.mc-cell-date {
  color: #5a5a5a;
  font-size: 11.5px;
}

.mc-empty {
  text-align: center;
  color: #9a9a96;
  padding: 16px;
}

.mc-opinion {
  margin: 0 0 10px;
  text-align: justify;
}

.mc-rules {
  margin: 28px 0 8px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.mc-rule {
  height: 1px;
  background-image: linear-gradient(
    to right,
    rgba(0, 0, 0, 0.18) 0 4px,
    transparent 4px 8px
  );
  background-size: 8px 1px;
  background-repeat: repeat-x;
  background-position: 0 100%;
}

.mc-sig {
  margin-top: 40px;
  display: flex;
  justify-content: flex-end;
}

.mc-sig-col {
  width: 300px;
  text-align: center;
  display: flex;
  flex-direction: column;
}

.mc-sig-line {
  border-bottom: 0.5px solid #161616;
  height: 44px;
  margin-bottom: 8px;
}

.mc-sig-name {
  font-weight: 600;
  font-size: 14px;
}

.mc-sig-role {
  font-size: 12.5px;
  color: #5a5a5a;
  margin-top: 2px;
}
</style>
